import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';

// components
import CardComponent from '../../signup/steps/payment/card-component';
import { ProteinFlavorField } from './address-fields/ProteinFlavorField';
import { ShippingAddressForm } from './address-fields/ShippingAddressFields';

// utils & types
import { EffectivePlanObjType } from '../types';
import { TagData } from 'src/store/signup-workflow/types';
import { Coupon } from '../../deprecated/payment/types';

// styles & assets
import colors from 'src/styles/equalution/colors';

// types
import { PaymentMethod } from '@stripe/stripe-js';

interface PaymentSectionProps {
  isMobile: boolean;
  showShippingAddress: boolean;
  isChallengePackageFlow: boolean;
  isSupplementAdded: boolean;
  isNutritionCoachingAdded: boolean;
  proteinFlavor: string | null;
  isUserLoading: boolean;
  effectivePlanObj: EffectivePlanObjType | undefined;
  selectedPlanId: string;
  submitDisabled: boolean;
  paymentMethod: PaymentMethod | undefined;
  coupon: Coupon | undefined;
  isChallengeAccepted: boolean;
  futureCommencement: boolean;
  tagId: number;
  challengeData: TagData | null;
  errorMessage: string;
  popupVisible: boolean;
  noIntentAvailable: boolean;
  stripePromise: any;
  findPlanName: (planId: string) => string;
  handleValidityChange: (isValid: boolean) => void;
  handleSubmit: () => Promise<void>;
  setPaymentMethod: (method: PaymentMethod | undefined) => void;
  setErrorMessage: (message: string) => void;
  setWalletPaymentIntentId: (id: string) => void;
  setNoIntentAvailable: (value: boolean) => void;
  shippingAddressFormRef: React.RefObject<any>;
  setProteinFlavorValue: (value: string) => void;
  activeTab: number;
  setActiveTab: (tab: number) => void;
  claimGift?: boolean;
  isSistemaGiftActive: boolean;
}

export const PaymentSection: React.FC<PaymentSectionProps> = ({
  isMobile,
  showShippingAddress,
  isChallengePackageFlow,
  isSupplementAdded,
  isSistemaGiftActive,
  isNutritionCoachingAdded,
  proteinFlavor,
  isUserLoading,
  effectivePlanObj,
  selectedPlanId,
  submitDisabled,
  paymentMethod,
  coupon,
  isChallengeAccepted,
  futureCommencement,
  tagId,
  challengeData,
  errorMessage,
  popupVisible,
  noIntentAvailable,
  stripePromise,
  findPlanName,
  handleValidityChange,
  handleSubmit,
  setPaymentMethod,
  setErrorMessage,
  setWalletPaymentIntentId,
  setNoIntentAvailable,
  shippingAddressFormRef,
  setProteinFlavorValue,
  activeTab,
  setActiveTab,
  claimGift = false,
}) => {
  return (
    <Stack
      minWidth={isMobile ? '320px' : '600px'}
      width={'100%'}
      flex={2}
      alignItems={'center'}
      sx={{
        marginInline: isMobile ? '32px' : '0px',
      }}
      gap={'32px'}>
      {showShippingAddress && (
        <>
          {isMobile && (
            <Typography
              alignSelf="center"
              sx={{
                fontSize: '20px',
                fontWeight: 700,
                color: colors.OFF_BLACK,
                textAlign: 'center',
                marginBottom: '8px',
                marginTop: '16px',
              }}>
              Confirm details
            </Typography>
          )}
          {!isSistemaGiftActive && (
            <ProteinFlavorField
              value={proteinFlavor}
              handleChange={value => setProteinFlavorValue(value)}
              disabled={isUserLoading}
            />
          )}
          <ShippingAddressForm
            onValidityChange={handleValidityChange}
            formRef={shippingAddressFormRef}
          />
        </>
      )}
      {(isMobile || isChallengePackageFlow || isSupplementAdded) && (
        <Typography
          alignSelf="center"
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            color: colors.OFF_BLACK,
            textAlign: 'center',
            marginTop: isMobile ? '16px' : 0,
          }}>
          Select payment
        </Typography>
      )}
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>
        {effectivePlanObj && (
          <Elements stripe={stripePromise}>
            <CardComponent
              selectedPlan={selectedPlanId}
              setPaymentMethod={setPaymentMethod}
              isSubmitDisabled={(() => {
                const shouldValidateProteinFlavorAndAddress =
                  showShippingAddress || (activeTab === 1 && isSupplementAdded);
                return (
                  shouldValidateProteinFlavorAndAddress &&
                  (submitDisabled ||
                    (isSistemaGiftActive ? false : !proteinFlavor))
                );
              })()}
              handleSubmit={handleSubmit}
              paymentMethod={paymentMethod}
              priceId={effectivePlanObj.id}
              finalPrice={parseFloat(effectivePlanObj.unitAmount.toString())}
              planName={findPlanName(selectedPlanId)}
              coupon={coupon}
              isChallengeAccepted={isChallengeAccepted}
              futureCommencement={futureCommencement}
              tagId={tagId}
              challengeObject={challengeData}
              productId={effectivePlanObj.productId}
              setErrorMessage={setErrorMessage}
              errorMessage={errorMessage}
              setWalletPaymentIntentId={setWalletPaymentIntentId}
              popupVisible={popupVisible}
              noIntentAvailable={noIntentAvailable}
              setNoIntentAvailable={setNoIntentAvailable}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              isSupplementAddedProp={isSupplementAdded}
              isNutritionCoachingAdded={isNutritionCoachingAdded}
              claimGift={claimGift}
              isSistemaGiftActive={isSistemaGiftActive}
            />
          </Elements>
        )}
      </Box>
    </Stack>
  );
};
